import { useCallback, useEffect, useRef, useState } from "react";
import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import type { MapRef } from "react-map-gl/maplibre";
import { bbox } from "@turf/turf";
import { BoundaryDraw } from "../draw/boundary-draw";
import { SELECT_DRAW_MODE } from "../draw/draw-modes";

type FeatureType = Feature<Geometry, GeoJsonProperties>;

interface UseBoundaryFeaturesOptions {
  draw: BoundaryDraw;
  mapRef: React.RefObject<MapRef | null> | React.MutableRefObject<MapRef | undefined>;
  initialGeometry?: GeoJSON.FeatureCollection;
  onGeometryChange: (geometry: GeoJSON.FeatureCollection | undefined) => void;
}

export function useBoundaryFeatures({
  draw,
  mapRef,
  initialGeometry,
  onGeometryChange,
}: UseBoundaryFeaturesOptions) {
  const [features, setFeatures] = useState<GeoJSON.Feature<GeoJSON.Geometry>[]>(
    initialGeometry?.features?.map((f) => f as GeoJSON.Feature<GeoJSON.Geometry>) ?? []
  );
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<string>(SELECT_DRAW_MODE);
  const [mapReady, setMapReady] = useState(false);
  const initialGeometryRef = useRef(initialGeometry);
  const hasLoadedInitialGeometry = useRef(false);
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;
  const hasSyncedFeatures = useRef(false);

  useEffect(() => {
    if (!hasSyncedFeatures.current) {
      hasSyncedFeatures.current = true;
      return;
    }
    onGeometryChangeRef.current(
      features.length === 0
        ? undefined
        : { type: "FeatureCollection", features },
    );
  }, [features]);

  const addOrUpdateFeatures = useCallback(
    (incoming: FeatureType | FeatureType[]) => {
      const incomingArray = Array.isArray(incoming) ? incoming : [incoming];
      setFeatures((prev) => {
        const existingIds = prev.map((f) => f.id as string);
        const newFeats = incomingArray.filter(
          (f) => !existingIds.includes(f.id as string)
        );
        const updateIds = incomingArray
          .filter((f) => existingIds.includes(f.id as string))
          .map((f) => f.id as string);

        let result = prev.map((f) => {
          if (updateIds.includes(f.id as string)) {
            const updated = incomingArray.find((u) => u.id === f.id)!;
            return {
              ...f,
              geometry: updated.geometry,
              properties: { ...updated.properties, name: f.properties?.name },
            };
          }
          return f;
        });

        return [...result, ...newFeats].map((f, i) => {
          if (!f.properties) f.properties = {};
          if (!f.properties.name) f.properties.name = `Boundary ${i + 1}`;
          return f;
        });
      });
    },
    []
  );

  const handleDelete = useCallback(
    (id: string) => {
      draw.delete([id]);
      setFeatures((prev) => prev.filter((f) => f.id !== id));
    },
    [draw]
  );

  const handleRename = useCallback((id: string, name: string) => {
    setFeatures((prev) =>
      prev.map((f) =>
        f.id === id && f.properties
          ? { ...f, properties: { ...f.properties, name } }
          : f
      )
    );
  }, []);

  const replaceFeatures = useCallback(
    (newFeatures: GeoJSON.Feature<GeoJSON.Geometry>[]) => {
      draw.deleteAll();
      const named = newFeatures.map((f, i) => {
        const clone = { ...f, properties: { ...f.properties } };
        if (!clone.properties!.name) clone.properties!.name = `Boundary ${i + 1}`;
        draw.add(clone);
        return clone;
      });
      setFeatures(named);
    },
    [draw],
  );

  const handleSelect = useCallback(
    (id: string) => {
      draw.select(id);
      setSelectedFeatureId(id);
    },
    [draw]
  );

  // Poll for map readiness
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      setMapReady(true);
      return;
    }
    const interval = setInterval(() => {
      if (mapRef.current) {
        setMapReady(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [mapRef]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    draw.attach(map);

    const unsubscribe = [
      draw.on("create", (e: { features: Feature[] }) => {
        addOrUpdateFeatures(e.features[0]);
      }),
      draw.on("update", (e: { features: Feature[] }) => {
        addOrUpdateFeatures(e.features);
      }),
      draw.on("selectionchange", (e: { features: Feature[] }) => {
        const first = e.features[e.features.length - 1];
        setSelectedFeatureId(first ? (first.id as string) : null);
      }),
      draw.on("modechange", (e: { mode: string }) => {
        setCurrentMode(e.mode);
      }),
    ];

    const initial = initialGeometryRef.current;
    if (
      !hasLoadedInitialGeometry.current &&
      initial?.features &&
      initial.features.length > 0
    ) {
      hasLoadedInitialGeometry.current = true;
      initial.features.forEach((f) => draw.add(f));
      const bounds = bbox(initial);
      mapRef.current?.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding: 40 },
      );
    }

    return () => unsubscribe.forEach((off) => off());
  }, [mapReady, draw, addOrUpdateFeatures, mapRef]);

  return {
    features,
    selectedFeatureId,
    currentMode,
    setCurrentMode,
    mapReady,
    addOrUpdateFeatures,
    replaceFeatures,
    handleDelete,
    handleRename,
    handleSelect,
  };
}

export const GEOMETRY_SIZE_LIMIT_KB = 360;
export const MAX_FEATURES = 20;

export async function compressedByteSize(obj: unknown): Promise<number> {
  const json = JSON.stringify(obj);
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"));
  const gzipped = await new Response(stream).arrayBuffer();
  return Math.ceil(gzipped.byteLength / 3) * 4;
}

export function useGeometryTooBig(features: GeoJSON.Feature[]): boolean {
  const [tooBig, setTooBig] = useState(false);
  useEffect(() => {
    if (features.length === 0) {
      setTooBig(false);
      return;
    }
    let cancelled = false;
    compressedByteSize({ features }).then((size) => {
      if (!cancelled) setTooBig(size > GEOMETRY_SIZE_LIMIT_KB * 1024);
    });
    return () => {
      cancelled = true;
    };
  }, [features]);
  return tooBig;
}

export function isTooManyFeatures(features: GeoJSON.Feature[]) {
  return features.length > MAX_FEATURES;
}
